import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import SeoHead from "@/components/SeoHead";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n/LanguageContext";
import {
  ArrowRight, Shield, Lock, AlertTriangle, HardHat, Signal, Wifi, Cpu,
  CheckCircle2, Quote, Headphones, Radio, Network, ShieldCheck, Activity,
  BookOpenCheck, Mic, Factory, Beaker, Droplets, Zap as Bolt, Pill, Container,
  Wrench, Building2, MessageSquare, ChevronRight, Plug, Database, Brain,
  FileDown, Download,
} from "lucide-react";

const trackDownload = (lang) => {
  try {
    const evt = lang === "en"
      ? "download_ruggededge_presentation_en"
      : "download_ruggededge_presentation_de";
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(evt);
    }
  } catch (_) {
    // tracking is best-effort
  }
};

const HERO_BG = "https://images.pexels.com/photos/37589838/pexels-photo-37589838.jpeg";
const DEVICE_IMG = "https://cdn.prod.website-files.com/648a1891f44afca175d3b615/648e3494052fafd80ac65895_edgeOne%20Front_lg.webp";

// Section helpers
const SectionLabel = ({ children, dark = false }) => (
  <div className={`text-xs uppercase tracking-[0.3em] font-mono-tech mb-3 ${dark ? "text-blue-400" : "text-blue-600"}`}>{children}</div>
);

const BADGE_ICONS = [Shield, AlertTriangle, Lock, HardHat, Signal, Wifi, Cpu];
const USECASE_ICONS = [Headphones, Radio, Network, ShieldCheck, Activity, BookOpenCheck, Mic];
const INDUSTRY_ICONS = [Droplets, Beaker, Factory, Bolt, Pill, Container, Wrench, Building2];
const WHY_ICONS = [MessageSquare, Cpu, ShieldCheck, Network, HardHat, Signal, CheckCircle2];

export default function LandingPage() {
  const { t, lang } = useLang();

  return (
    <div className="min-h-screen bg-white">
      <SeoHead />
      <Navbar dark />

      {/* ─────────── 1. HERO ─────────── */}
      <section data-testid="hero-section" className="relative min-h-[94vh] flex items-center bg-[#0B1121] text-white overflow-hidden pt-24">
        <div className="absolute inset-0 bg-cover bg-center opacity-45" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1121] via-[#0B1121]/85 to-[#0B1121]/30" />
        <div className="absolute inset-0 tech-grid-bg-dark opacity-40" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-10 py-16">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-xs uppercase tracking-[0.25em] font-mono-tech text-blue-300 mb-8" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 bg-blue-500 rounded-full animate-pulse" /> {t.hero.eyebrow}
              </div>
              <h1 className="font-display font-black text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight" data-testid="hero-h1">
                {t.hero.h1}
              </h1>
              <p className="mt-7 max-w-2xl text-lg md:text-xl text-slate-200 leading-relaxed">{t.hero.sub}</p>
              <p className="mt-4 max-w-2xl text-base text-slate-400 leading-relaxed">{t.hero.text}</p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a href="#contact" data-testid="hero-cta-primary" className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 font-semibold hover:bg-blue-700 transition-all">
                  {t.hero.ctaPrimary} <ArrowRight size={18} />
                </a>
                <a href="#use-cases" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center gap-2 border border-white/30 px-8 py-4 font-semibold text-white hover:border-blue-500 hover:text-blue-400 transition-all">
                  {t.hero.ctaSecondary}
                </a>
              </div>

              <div className="mt-12 flex flex-wrap gap-2 max-w-2xl">
                {t.hero.badges.map((b, i) => {
                  const Ic = BADGE_ICONS[i % BADGE_ICONS.length];
                  return (
                    <span key={b} className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] px-3 py-2 text-[11px] uppercase tracking-widest font-mono-tech text-slate-200">
                      <Ic size={14} className="text-blue-400" /> {b}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-500/10 blur-3xl rounded-full" />
                <div className="relative border border-white/15 bg-white/[0.04] backdrop-blur-xl p-8">
                  <div className="absolute -top-3 left-6 bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-widest font-mono-tech">RuggedEdge EdgeOne™</div>
                  <img src={DEVICE_IMG} alt="EdgeOne — ATEX 5G Edge Gateway" className="w-[260px] md:w-[340px] h-auto object-contain mx-auto" data-testid="hero-device-image" loading="eager" decoding="async" width="340" height="340" fetchPriority="high" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── 2. TRUST BAR ─────────── */}
      <section className="bg-white border-y border-slate-200 py-10" data-testid="trust-section">
        <div className="text-center text-xs uppercase tracking-[0.3em] font-mono-tech text-slate-500 mb-6">{t.trust.headline}</div>
        <div className="mx-auto max-w-6xl px-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {t.trust.logos.map((p) => (
            <div key={p} className="font-display font-bold text-xl text-slate-400 hover:text-slate-700 transition-colors whitespace-nowrap">{p}</div>
          ))}
        </div>
      </section>

      {/* ─────────── 3. PROBLEM ─────────── */}
      <section id="problem" className="py-24 bg-white" data-testid="problem-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <SectionLabel>{t.problem.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.problem.headline}</h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.problem.intro}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {t.problem.cards.map((p, i) => (
              <div key={p.title} className="bg-white p-7 flex flex-col" data-testid={`problem-card-${i}`}>
                <div className="text-blue-600 font-display font-black text-xs font-mono-tech mb-4">0{i + 1}</div>
                <h3 className="font-display font-bold text-xl mb-3 leading-snug">{p.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 border-l-4 border-blue-600 pl-6 max-w-3xl">
            <Quote size={28} className="text-blue-600 mb-3" />
            <p className="text-xl text-slate-700 italic leading-relaxed">{t.problem.quote}</p>
            <p className="mt-3 text-sm font-mono-tech uppercase tracking-widest text-slate-500">{t.problem.quoteAttr}</p>
          </div>
        </div>
      </section>

      {/* ─────────── 4. SOLUTION ─────────── */}
      <section id="solution" className="py-24 bg-slate-50 border-y border-slate-200" data-testid="solution-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <SectionLabel>{t.solution.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.solution.headline}</h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">{t.solution.intro}</p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">{t.solution.text1}</p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">{t.solution.text2}</p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {t.solution.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-800">
                  <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-sm leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>

            <a href="#contact" data-testid="solution-cta" className="inline-flex items-center gap-2 mt-10 bg-blue-600 text-white px-6 py-3 font-semibold hover:bg-blue-700 transition-colors">
              {t.solution.cta} <ArrowRight size={18} />
            </a>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] bg-white border border-slate-200 p-10 flex items-center justify-center overflow-hidden">
              <img src={DEVICE_IMG} alt="EdgeOne" className="max-h-[460px] object-contain" loading="lazy" decoding="async" width="460" height="460" />
              <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-widest font-mono-tech">RuggedEdge EdgeOne™</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── 4b. PLATFORM (Connect. Collect. Context.) ─────────── */}
      <section id="platform" className="py-24 bg-[#0B1121] text-white relative overflow-hidden" data-testid="platform-section">
        <div className="absolute inset-0 tech-grid-bg-dark opacity-30" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <SectionLabel dark>{t.platform.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.platform.headline}</h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">{t.platform.intro}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {t.platform.pillars.map((p, i) => {
              const PIcon = [Plug, Database, Brain][i];
              return (
                <div key={p.tag} className="bg-[#0B1121] p-8 flex flex-col" data-testid={`platform-pillar-${i}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-11 w-11 bg-blue-600 text-white flex items-center justify-center">
                      <PIcon size={20} />
                    </div>
                    <div className="text-xs uppercase tracking-[0.25em] font-mono-tech text-blue-400">{p.tag}</div>
                  </div>
                  <h3 className="font-display font-bold text-xl mb-3 leading-snug">{p.title}</h3>
                  <p className="text-slate-300 leading-relaxed text-sm">{p.text}</p>
                </div>
              );
            })}
          </div>

          <p className="mt-10 max-w-3xl text-base text-slate-400 leading-relaxed border-l-4 border-blue-600 pl-4">{t.platform.closing}</p>
        </div>
      </section>

      {/* ─────────── 4c. PDF DOWNLOAD CTA ─────────── */}
      <section id="download" className="bg-white border-b border-slate-200 py-14" data-testid="download-section" aria-labelledby="download-h">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="border border-slate-200 bg-slate-50 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
            <div className="shrink-0 hidden md:flex h-16 w-16 items-center justify-center bg-blue-600 text-white">
              <FileDown size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.3em] font-mono-tech text-blue-600 mb-2">{t.download.label}</div>
              <h2 id="download-h" className="font-display font-bold text-xl md:text-2xl leading-snug text-slate-900" data-testid="download-headline">
                {t.download.headline}
              </h2>
              <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed">{t.download.text}</p>
              <p className="mt-2 text-xs md:text-sm text-slate-500 italic">{t.download.note}</p>
            </div>
            <div className="shrink-0 flex flex-col items-start md:items-end gap-2">
              <a
                href={t.download.file}
                download
                onClick={() => trackDownload(lang)}
                data-testid="download-cta-primary"
                data-event={lang === "en" ? "download_ruggededge_presentation_en" : "download_ruggededge_presentation_de"}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <Download size={18} /> {t.download.cta}
              </a>
              <span className="text-[11px] uppercase tracking-widest font-mono-tech text-slate-500">{t.download.meta}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── 5. USE CASES ─────────── */}
      <section id="use-cases" className="py-24 bg-white" data-testid="usecases-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <SectionLabel>{t.useCases.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.useCases.headline}</h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.useCases.intro}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.useCases.cards.map((uc, i) => {
              const UIcon = USECASE_ICONS[i % USECASE_ICONS.length];
              return (
                <article key={uc.key} className="bg-white border border-slate-200 p-7 flex flex-col hover:border-blue-500 transition-colors" data-testid={`usecase-card-${uc.key}`}>
                  <div className="h-11 w-11 bg-slate-900 text-white flex items-center justify-center mb-5">
                    <UIcon size={20} />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-3 leading-snug">{uc.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm flex-1">{uc.text}</p>

                  <Accordion type="single" collapsible className="mt-5 border-t border-slate-100">
                    <AccordionItem value="typical" className="border-0">
                      <AccordionTrigger className="text-xs font-mono-tech uppercase tracking-widest text-blue-600 py-3 hover:no-underline" data-testid={`usecase-accordion-${uc.key}`}>
                        {t.useCases.typicalLabel}
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <ul className="space-y-2">
                          {uc.typical.map((tp) => (
                            <li key={tp} className="flex items-start gap-2 text-sm text-slate-700">
                              <ChevronRight size={14} className="text-blue-600 mt-1 shrink-0" /> {tp}
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <a href="#contact" data-testid={`usecase-cta-${uc.key}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700">
                    {uc.cta} <ArrowRight size={14} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── 5b. PILOT TEASER (mid-page CTA) ─────────── */}
      <section className="bg-blue-600 text-white" data-testid="pilot-teaser-section" aria-labelledby="pilot-teaser-h">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="md:max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-white/70 mb-3">{t.pilot.label}</div>
            <h2 id="pilot-teaser-h" className="font-display font-black text-2xl md:text-3xl tracking-tight leading-snug">{t.pilot.teaserHeadline}</h2>
            <p className="mt-3 text-base md:text-lg text-white/90 leading-relaxed">{t.pilot.teaserText}</p>
          </div>
          <a href="#contact" data-testid="pilot-teaser-cta" className="shrink-0 inline-flex items-center justify-center gap-2 bg-white text-blue-700 px-7 py-4 font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap">
            {t.pilot.teaserCta} <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* ─────────── 6. INDUSTRIES ─────────── */}
      <section id="industries" className="py-24 bg-slate-50 border-y border-slate-200" data-testid="industries-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <SectionLabel>{t.industries.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.industries.headline}</h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.industries.intro}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {t.industries.cards.map((ind, i) => {
              const IIcon = INDUSTRY_ICONS[i % INDUSTRY_ICONS.length];
              return (
                <div key={ind.title} className="bg-white p-6" data-testid={`industry-card-${i}`}>
                  <IIcon size={22} className="text-blue-600 mb-4" />
                  <h3 className="font-display font-bold text-base mb-2 leading-snug">{ind.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{ind.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── 7. PILOT PROJECT ─────────── */}
      <section id="pilot" className="py-24 bg-[#0B1121] text-white relative overflow-hidden" data-testid="pilot-section">
        <div className="absolute inset-0 tech-grid-bg-dark opacity-30" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel dark>{t.pilot.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">{t.pilot.headline}</h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">{t.pilot.intro}</p>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">{t.pilot.sub}</p>
            <a href="#contact" data-testid="pilot-cta" className="inline-flex items-center gap-2 mt-8 bg-blue-600 text-white px-7 py-4 font-semibold hover:bg-blue-700 transition-colors">
              {t.pilot.cta} <ArrowRight size={18} />
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-white/15 bg-white/[0.04] p-8">
              <div className="text-xs uppercase tracking-widest font-mono-tech text-blue-400 mb-6">{t.pilot.includesLabel}</div>
              <ul className="space-y-4">
                {t.pilot.includes.map((it, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="font-display font-black text-blue-500 text-sm shrink-0 w-7 font-mono-tech">{String(i + 1).padStart(2, "0")}</div>
                    <span className="text-slate-200 leading-relaxed text-[15px]">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── 8. TECHNICAL DATA ─────────── */}
      <section id="specs" className="py-24 bg-white" data-testid="specs-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <SectionLabel>{t.specs.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.specs.headline}</h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.specs.intro}</p>
          </div>

          <div className="border border-slate-200">
            <Accordion type="multiple" defaultValue={t.specs.rows.slice(0, 4).map((r) => r.k)}>
              {t.specs.rows.map((row) => (
                <AccordionItem value={row.k} key={row.k} className="border-b border-slate-200 last:border-0 px-6">
                  <AccordionTrigger className="font-display font-bold text-base text-slate-900 py-5 hover:no-underline">
                    <span className="font-mono-tech text-xs uppercase tracking-widest text-blue-600 mr-4">{row.k}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 text-sm leading-relaxed pb-5">{row.v}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-8">
            <a href="/products/edgeone" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700" data-testid="specs-cta">
              {t.specs.cta} <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ─────────── 9. WHY ALEGER ─────────── */}
      <section id="why" className="py-24 bg-slate-50 border-y border-slate-200" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <SectionLabel>{t.why.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">{t.why.headline}</h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.why.intro}</p>
            <p className="mt-4 text-base text-slate-700 leading-relaxed font-medium border-l-4 border-blue-600 pl-4">{t.why.positioning}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {t.why.cards.map((c, i) => {
              const WIcon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div key={c.title} className="bg-white p-7" data-testid={`why-card-${i}`}>
                  <WIcon size={22} className="text-blue-600 mb-4" />
                  <h3 className="font-display font-bold text-lg mb-3 leading-snug">{c.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{c.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10">
            <a href="#contact" data-testid="why-cta" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-semibold hover:bg-blue-700 transition-colors">
              {t.why.cta} <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* ─────────── 10. FAQ ─────────── */}
      <section id="faq" className="py-24 bg-white" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <SectionLabel>{t.faq.label}</SectionLabel>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-12">{t.faq.headline}</h2>
          <Accordion type="single" collapsible className="border-t border-slate-200">
            {t.faq.items.map((f, idx) => (
              <AccordionItem value={`item-${idx}`} key={idx} className="border-b border-slate-200" data-testid={`faq-item-${idx}`}>
                <AccordionTrigger className="text-left font-display font-bold text-base md:text-lg py-6 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-6 text-sm md:text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ─────────── 11. CONTACT ─────────── */}
      <section id="contact" className="py-24 bg-[#0B1121] text-white relative overflow-hidden" data-testid="contact-section">
        <div className="absolute inset-0 tech-grid-bg-dark opacity-30" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionLabel dark>{t.contact.label}</SectionLabel>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight">{t.contact.headline}</h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">{t.contact.intro}</p>

            <div className="mt-10 space-y-3 text-sm text-slate-300">
              <div className="flex items-center gap-3"><span className="text-blue-500 font-mono-tech text-xs uppercase tracking-widest w-20">{t.contact.info.emailLabel}</span> info@alegerglobal.com</div>
              <div className="flex items-center gap-3"><span className="text-blue-500 font-mono-tech text-xs uppercase tracking-widest w-20">{t.contact.info.phoneLabel}</span> +43 7612 219 6010</div>
              <div className="flex items-center gap-3"><span className="text-blue-500 font-mono-tech text-xs uppercase tracking-widest w-20">{t.contact.info.hqLabel}</span> {t.contact.info.hq}</div>
            </div>

            <div className="mt-10 border-t border-white/10 pt-6 max-w-md" data-testid="contact-download-block">
              <div className="text-xs font-mono-tech uppercase tracking-widest text-blue-400 mb-2">{t.download.secondaryHeadline}</div>
              <p className="text-sm text-slate-300 leading-relaxed">{t.download.secondaryText}</p>
              <a
                href={t.download.file}
                download
                onClick={() => trackDownload(lang)}
                data-testid="contact-download-cta"
                data-event={lang === "en" ? "download_ruggededge_presentation_en" : "download_ruggededge_presentation_de"}
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
              >
                <Download size={14} /> {t.download.secondaryCta}
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <LeadForm variant="dark" source="homepage-contact" productInterest="EdgeOne" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
